import React, { useState, useEffect } from 'react';
import { cryptoAPI } from '../lib/api';
import { formatNumber, getRiskClass, truncateAddress } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  Eye,
  Filter,
  RefreshCw,
  Bitcoin,
  Wallet,
  AlertTriangle,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';

const BLOCKCHAINS = [
  { value: 'bitcoin', label: 'Bitcoin', symbol: 'BTC', color: '#F7931A' },
  { value: 'ethereum', label: 'Ethereum', symbol: 'ETH', color: '#627EEA' },
  { value: 'tron', label: 'Tron', symbol: 'TRX', color: '#FF0013' },
  { value: 'binance', label: 'BSC', symbol: 'BNB', color: '#F0B90B' },
];

const RISK_LEVELS = ['critical', 'high', 'medium', 'low'];

export default function CryptoIntel() {
  const [wallets, setWallets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [blockchain, setBlockchain] = useState('');
  const [riskLevel, setRiskLevel] = useState('');
  const [page, setPage] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWallet, setNewWallet] = useState({
    address: '',
    blockchain: 'bitcoin',
    tags: '',
  });

  const limit = 20;

  useEffect(() => {
    fetchWallets();
  }, [page, blockchain, riskLevel]);

  const fetchWallets = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        ...(blockchain && { blockchain }),
        ...(riskLevel && { risk_level: riskLevel }),
      };
      const response = await cryptoAPI.listWallets(params);
      setWallets(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch wallets:', error);
      toast.error('Failed to load wallets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWallet = async (e) => {
    e.preventDefault();
    try {
      const walletData = {
        ...newWallet,
        tags: newWallet.tags ? newWallet.tags.split(',').map((t) => t.trim()) : [],
      };
      await cryptoAPI.createWallet(walletData);
      toast.success('Wallet added successfully');
      setShowCreateModal(false);
      setNewWallet({ address: '', blockchain: 'bitcoin', tags: '' });
      fetchWallets();
    } catch (error) {
      toast.error('Failed to add wallet');
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success('Address copied to clipboard');
  };

  const getBlockchainInfo = (chain) => {
    return BLOCKCHAINS.find((b) => b.value === chain) || BLOCKCHAINS[0];
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="crypto-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-secondary text-2xl font-bold uppercase tracking-wide text-white">
            Crypto Intelligence
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Blockchain analytics and wallet tracing
          </p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-wallet-btn">
              <Plus className="w-4 h-4" />
              Track Wallet
            </Button>
          </DialogTrigger>
          <DialogContent className="glass border-border">
            <DialogHeader>
              <DialogTitle className="font-secondary text-lg font-bold uppercase tracking-wide">
                Add Wallet to Track
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateWallet} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="label-mono">Wallet Address</Label>
                <Input
                  value={newWallet.address}
                  onChange={(e) => setNewWallet({ ...newWallet, address: e.target.value })}
                  placeholder="0x... or bc1..."
                  className="bg-black/20 border-white/10 font-mono text-sm"
                  required
                  data-testid="wallet-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-mono">Blockchain</Label>
                <Select
                  value={newWallet.blockchain}
                  onValueChange={(value) => setNewWallet({ ...newWallet, blockchain: value })}
                >
                  <SelectTrigger className="bg-black/20 border-white/10" data-testid="blockchain-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {BLOCKCHAINS.map((chain) => (
                      <SelectItem key={chain.value} value={chain.value}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: chain.color }}
                          />
                          {chain.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-mono">Tags (comma-separated)</Label>
                <Input
                  value={newWallet.tags}
                  onChange={(e) => setNewWallet({ ...newWallet, tags: e.target.value })}
                  placeholder="exchange, suspicious, mixer"
                  className="bg-black/20 border-white/10"
                  data-testid="wallet-tags-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-wallet-btn">
                  Add Wallet
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {BLOCKCHAINS.map((chain) => (
          <Card key={chain.value} className="glass card-hover">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded flex items-center justify-center"
                  style={{ backgroundColor: `${chain.color}20` }}
                >
                  <Bitcoin className="w-5 h-5" style={{ color: chain.color }} />
                </div>
                <div>
                  <p className="font-secondary text-lg font-bold text-white">
                    {wallets.filter((w) => w.blockchain === chain.value).length}
                  </p>
                  <p className="text-xs text-muted-foreground">{chain.label} Wallets</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <Select value={blockchain || "all"} onValueChange={(v) => setBlockchain(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10" data-testid="filter-blockchain">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Blockchain" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Chains</SelectItem>
                {BLOCKCHAINS.map((chain) => (
                  <SelectItem key={chain.value} value={chain.value}>
                    {chain.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={riskLevel || "all"} onValueChange={(v) => setRiskLevel(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px] bg-black/20 border-white/10" data-testid="filter-crypto-risk">
                <AlertTriangle className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Levels</SelectItem>
                {RISK_LEVELS.map((level) => (
                  <SelectItem key={level} value={level}>
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              onClick={() => {
                setBlockchain('');
                setRiskLevel('');
                setPage(0);
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Wallets Table */}
      <Card className="glass">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading wallets...</p>
            </div>
          ) : wallets.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead>Address</TableHead>
                  <TableHead>Blockchain</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Transactions</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wallets.map((wallet) => {
                  const chainInfo = getBlockchainInfo(wallet.blockchain);
                  return (
                    <TableRow key={wallet.id} className="border-white/5">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-white">
                            {truncateAddress(wallet.address, 8, 6)}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => copyAddress(wallet.address)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: chainInfo.color }}
                          />
                          <span className="text-xs">{chainInfo.symbol}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-white">
                        {wallet.balance?.toFixed(4) || '0.0000'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`${getRiskClass(wallet.risk_level)} text-[10px] font-mono uppercase`}
                        >
                          {wallet.risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {formatNumber(wallet.transaction_count)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {wallet.tags?.slice(0, 3).map((tag, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="text-[10px] font-mono"
                            >
                              {tag}
                            </Badge>
                          ))}
                          {wallet.tags?.length > 3 && (
                            <Badge variant="outline" className="text-[10px]">
                              +{wallet.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" data-testid={`view-wallet-${wallet.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center">
              <Wallet className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No wallets tracked</p>
              <p className="text-sm text-muted-foreground/50 mt-1">
                Add a wallet address to start tracking
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(page + 1) * limit >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
